import React, { useEffect, useMemo } from 'react';
import {
  Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText,
  Box, Typography, Divider, IconButton, Tooltip, useMediaQuery, useTheme, Paper, Toolbar
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  ListAlt as ListIcon,
  Add as AddIcon,
  Business as BusinessIcon,
  Assessment as ReportIcon,
  Person as ProfileIcon,
  Settings as SettingsIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Support as SupportIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

interface SidebarProps {
  mobileOpen: boolean;
  onMobileToggle: () => void;
  collapsed: boolean;
  onCollapseToggle: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ mobileOpen, onMobileToggle, collapsed, onCollapseToggle }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, hasPermission } = useAuth();

  const drawerWidth = collapsed ? 68 : 265;

  // mobilde rota değişince kapat
  useEffect(() => {
    if (isMobile && mobileOpen) onMobileToggle();
  }, [location.pathname, isMobile, mobileOpen, onMobileToggle]);

  const can = useMemo(() => {
    const role = user?.role ?? '';
    const has = (r: string) => (typeof hasPermission === 'function' ? hasPermission(r) : role === r || role === 'admin');
    return {
      admin: has('admin'),
      support: role === 'support' || role === 'admin',
      customer: role === 'customer',
    };
  }, [user, hasPermission]);

  const menuItems = useMemo(() => {
    const list = [
      { text: 'Destek Taleplerim', icon: <ListIcon sx={{ fontSize: 22 }} />, path: '/tickets', roles: ['admin', 'support', 'customer'], tip: 'Tüm talepleriniz' },
    ];

    if (can.admin) {
      list.unshift({ text: 'Anasayfa', icon: <DashboardIcon sx={{ fontSize: 22 }} />, path: '/dashboard', roles: ['admin'], tip: 'Sistem istatistikleri' });
      list.push({ text: 'Firma Yönetimi', icon: <BusinessIcon sx={{ fontSize: 22 }} />, path: '/companies', roles: ['admin'], tip: 'Firma yönetimi' });
    }
    if (can.support) {
      list.push({ text: 'Raporlar', icon: <ReportIcon sx={{ fontSize: 22 }} />, path: '/reports', roles: ['admin', 'support'], tip: 'Detaylı raporlar' });
    }

    const role = user?.role ?? '';
    return list.filter(i => i.roles.includes(role));
  }, [can, user?.role]);

  const userMenu = [
    { text: 'Profilim', icon: <ProfileIcon sx={{ fontSize: 22 }} />, path: '/profile', tip: 'Profil bilgileriniz' },
    { text: 'Ayarlar',   icon: <SettingsIcon sx={{ fontSize: 22 }} />, path: '/settings', tip: 'Sistem ayarları' },
  ];

  const handleNav = (path: string) => {
    navigate(path);
    if (isMobile) onMobileToggle();
  };

const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Paper 
      elevation={6} 
      square 
      sx={{ 
        height: '100%', 
        background: 'linear-gradient(180deg, #2D4E66 0%, #1e293b 100%)', // YENİ RENK!
        color: 'white',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {children}
    </Paper>
  );

  const drawerInner = (
    <Shell>
      <Toolbar sx={{ minHeight: { xs: 56, md: 64 } }} />

      {/* Kullanıcı kartı */}
      {!collapsed && (
        <Box sx={{ 
          p: 0,
          borderBottom: '1px solid rgba(255,255,255,0.12)', 
          background: 'rgba(255,255,255,0.05)',
          height: 80
        }}>
          <Box 
            sx={{ 
              width: '100%',
              height: '100%',
              background: `url(/sidebar3.jpg) center/cover no-repeat`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          />
        </Box>
      )}

      {/* MENÜ */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', py: 1 }}>
        <List sx={{ px: 1 }}>
          {menuItems.map((item) => (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
              <Tooltip title={collapsed ? item.tip : ''} placement="right">
                <ListItemButton
                  selected={location.pathname === item.path}
                  onClick={() => handleNav(item.path)}
                  sx={{
                    borderRadius: 2,
                    justifyContent: collapsed ? 'center' : 'initial',
                    px: collapsed ? 1.5 : 2,
                    minHeight: 48,
                    '&.Mui-selected': {
                      background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                      color: 'white',
                      boxShadow: '0 4px 14px rgba(59,130,246,0.4)',
                      '&:hover': { background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)' },
                    },
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
                    transition: 'all 0.25s ease',
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 0, mr: collapsed ? 'auto' : 2, justifyContent: 'center', color: 'inherit' }}>
                    {item.icon}
                  </ListItemIcon>
                  {!collapsed && (
                    <ListItemText
                      primary={item.text}
                      primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: location.pathname === item.path ? 700 : 500 }}
                    />
                  )}
                </ListItemButton>
              </Tooltip>
            </ListItem>
          ))}
        </List>
      </Box>

      {/* ALTTAN KISIM */}
      <Box sx={{ mt: 'auto' }}>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.15)' }} />
        <List sx={{ px: 1, py: 1 }}>
          {userMenu.map((item) => (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
              <Tooltip title={collapsed ? item.tip : ''} placement="right">
                <ListItemButton
                  selected={location.pathname === item.path}
                  onClick={() => handleNav(item.path)}
                  sx={{
                    borderRadius: 2,
                    justifyContent: collapsed ? 'center' : 'initial',
                    px: collapsed ? 1.5 : 2,
                    minHeight: 48,
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
                    '&.Mui-selected': { backgroundColor: 'rgba(255,255,255,0.18)' },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 0, mr: collapsed ? 'auto' : 2, justifyContent: 'center', color: 'inherit' }}>
                    {item.icon}
                  </ListItemIcon>
                  {!collapsed && (
                    <ListItemText
                      primary={item.text}
                      primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: location.pathname === item.path ? 700 : 500 }}
                    />
                  )}
                </ListItemButton>
              </Tooltip>
            </ListItem>
          ))}

          {/* DARALTMA BUTONU - YENİ RENK UYUMLU */}
<ListItem disablePadding sx={{ mt: 0.5 }}>
  <Tooltip title={collapsed ? 'Genişlet' : 'Daralt'} placement="right">
    <ListItemButton
      onClick={onCollapseToggle}
      sx={{
        borderRadius: 1.5,
        justifyContent: collapsed ? 'center' : 'flex-start',
        px: collapsed ? 1.2 : 1.8,
        py: 1,
        minHeight: 40,
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.25s ease',

        // NORMAL HAL
        backgroundColor: 'transparent',
        border: '1px solid rgba(30, 82, 219, 0.2)',

        // HOVER HALİ - TAM #1E52DB KAPLIYOR!
        '&:hover': {
          backgroundColor: '#1E52DB',
          borderColor: '#1E52DB',
          '& .collapse-text': {
            color: 'white !important',
          },
          '& .collapse-circle': {
            backgroundColor: 'rgba(255, 255, 255, 0.25)',
            color: 'white',
            boxShadow: '0 0 12px rgba(255, 255, 255, 0.3)',
          },
        },
      }}
    >
      {/* Daire */}
      <Box 
        className="collapse-circle"
        sx={{ 
          width: 22,
          height: 22,
          borderRadius: '50%',
          backgroundColor: 'rgba(30, 82, 219, 0.15)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#1E52DB',
          fontSize: '18px',
          mr: collapsed ? 0 : 1.2,
          transition: 'all 0.25s ease',
        }}
      >
        {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
      </Box>

      {/* Yazı */}
      {!collapsed && (
        <Typography 
          className="collapse-text"
          variant="caption" 
          sx={{ 
            color: '#94a3b8',
            fontWeight: 500,
            fontSize: '0.8rem',
            transition: 'color 0.25s ease',
          }}
        >
          Menüyü Daralt
        </Typography>
      )}
    </ListItemButton>
  </Tooltip>
</ListItem>
        </List>
      </Box>
    </Shell>
  );

  return (
    <>
      {/* Mobile */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { 
            width: 280, 
            border: 'none', 
            boxShadow: '8px 0 30px rgba(0,0,0,0.4)' 
          },
        }}
      >
        {drawerInner}
      </Drawer>

      {/* Desktop */}
      <Drawer
        variant="permanent"
        open
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            border: 'none',
            overflowX: 'hidden',
            boxShadow: '6px 0 25px rgba(0,0,0,0.15)',
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.easeOut,
              duration: theme.transitions.duration.enteringScreen,
            }),
          },
        }}
      >
        {drawerInner}
      </Drawer>
    </>
  );
};

export default Sidebar;